import { EventEmitter2 } from "eventemitter2";
import { jwtDecode } from "jwt-decode";
import Keycloak, { KeycloakError, KeycloakLogoutOptions, KeycloakPromise } from "keycloak-js";
import { config } from "config";
import { TokenData } from "./types";

export type AresKeycloakEvents =
  "onReady"
  | "onAuthSuccess"
  | "onAuthError"
  | "onAuthRefreshSuccess"
  | "onAuthRefreshError"
  | "onAuthLogout"
  | "onTokenExpired"
  | "onActionUpdate";

export class AresKeycloak extends Keycloak {
  private emitter = new EventEmitter2({
    wildcard: true,
  });

  public constructor() {
    super({
      url: config.keycloakUrl!,
      realm: config.keycloakRealm!,
      clientId: config.keycloakClientId!,
    });

    document.addEventListener("visibilitychange", ev => {
      if (document.visibilityState === "visible") {
        this.refreshUserToken(Number.MAX_VALUE);
      }
    });
  }

  public initWithDefaults() {
    return this.init({
      onLoad: "check-sso",
      silentCheckSsoRedirectUri: window.location.origin + "/silent-check-sso.html",
      silentCheckSsoFallback: false,
    });
  }

  public override logout(options?: KeycloakLogoutOptions): KeycloakPromise<void, void> {
    localStorage.clear();
    sessionStorage.clear();
    return super.logout(options);
  }

  public async refreshUserToken(minValidity = 30) {
    if (this.authenticated) {
      try {
        await this.updateToken(minValidity);
      } catch (e) {
        console.error(e);
        await this.logout();
      }
    }
  }

  public addListener(eventName: AresKeycloakEvents | "*", listener: (...args: any[]) => void) {
    this.emitter.addListener(eventName, listener);
  }

  public removeListener(eventName: AresKeycloakEvents | "*", listener: (...args: any[]) => void) {
    this.emitter.removeListener(eventName, listener);
  }

  public async getKeycloakToken() {
    await this.refreshUserToken(30);
    return this.token;
  }

  public getTokenData(): TokenData {
    try {
      return jwtDecode<TokenData>(this.token || "");
    } catch {
      return {};
    }
  }

  private emit(event: AresKeycloakEvents, options?: Object) {
    this.emitter.emit(event, {
      event,
      ...options,
    });
  }

  override onReady(authenticated?: boolean) {
    this.emit("onReady", { authenticated });
  }

  override onAuthSuccess() {
    this.emit("onAuthSuccess");
  }

  override onAuthError(errorData: KeycloakError) {
    this.emit("onAuthError", { errorData });
  }

  override onAuthRefreshSuccess() {
    this.emit("onAuthRefreshSuccess");
  }

  override onAuthRefreshError() {
    this.emit("onAuthRefreshError");
  }

  override onAuthLogout() {
    this.emit("onAuthLogout");
  }

  override onTokenExpired() {
    this.emit("onTokenExpired");
  }

  override onActionUpdate(status: "success" | "cancelled" | "error") {
    this.emit("onActionUpdate", { status });
  }
}

export const keycloakInstance = new AresKeycloak();